import { FC, useCallback, useEffect, useMemo } from 'react';
import { APP_ENV_CONFIG } from '../config';
import { INTERVIEW } from './Interview/IDs';
import {
  RemoteProcedureCall,
  useApplicationStateLoader,
  useApplicationStateValue,
} from '@resistdesign/voltra/app/utils';
import { USER } from './IDs';
import { InterviewStatus, UserInfo } from '../../common/Types';
import { InterviewChatForm } from './Interview/InterviewChatForm';
import { ExpiredMessage } from './Interview/ExpiredMessage';

const {
  api: { domain: API_DOMAIN },
} = APP_ENV_CONFIG;

export type InterviewProps = {
  authorization: string;
};

export const Interview: FC<InterviewProps> = ({ authorization }) => {
  const ADD_MESSAGE_RPC: RemoteProcedureCall = useMemo(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/user/interview',
        authorization,
      },
      path: '/create-message',
    }),
    [authorization]
  );
  const MESSAGES_RPC: RemoteProcedureCall = useMemo(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/user/interview',
        authorization,
      },
      path: '/list-messages',
    }),
    [authorization]
  );
  const USER_INFO_RPC: RemoteProcedureCall = useMemo(
    () => ({
      serviceConfig: {
        protocol: 'https',
        domain: API_DOMAIN,
        basePath: '/user',
        authorization,
      },
      path: '/info',
    }),
    [authorization]
  );
  const {
    value: messagesToWatch,
    value: messages = [],
    onChange: onMessagesChange,
  } = useApplicationStateValue(INTERVIEW.MESSAGES);
  const { loading: addMessageLoading, makeRemoteProcedureCall: addMessage } = useApplicationStateLoader({
    identifier: INTERVIEW.ADD_MESSAGE,
    remoteProcedureCall: ADD_MESSAGE_RPC,
    manual: true,
  });
  const { loading: messagesLoading, invalidate: invalidateMessages } = useApplicationStateLoader({
    identifier: INTERVIEW.MESSAGES,
    remoteProcedureCall: MESSAGES_RPC,
  });
  const { loading: userInfoLoading, invalidate: invalidateUserInfo } = useApplicationStateLoader({
    identifier: USER.INFO,
    remoteProcedureCall: USER_INFO_RPC,
  });
  const { value: userInfo } = useApplicationStateValue(USER.INFO);
  const { status = InterviewStatus.INTERVIEW_NOT_STARTED }: Partial<UserInfo> = userInfo || {};
  const onAddMessage = useCallback(
    async (newMessage: string) => {
      onMessagesChange([
        ...messages,
        {
          role: 'user',
          content: newMessage,
        },
      ]);
      await addMessage?.(newMessage);
      invalidateMessages();
    },
    [addMessage, invalidateMessages, messages, onMessagesChange]
  );
  const isLoading = addMessageLoading || messagesLoading || userInfoLoading;

  useEffect(() => {
    invalidateUserInfo();
  }, [messagesToWatch]);

  return status !== InterviewStatus.INTERVIEW_EXPIRED ? (
    <InterviewChatForm
      disabled={status === InterviewStatus.INTERVIEW_COMPLETED}
      messagesLoading={isLoading}
      messages={messages}
      onAddMessage={onAddMessage}
    />
  ) : (
    <ExpiredMessage />
  );
};
